import { getCampaignListFiltersStorageKey } from '@features/campaigns/list/consts/localStorageKeys.ts';
import { CampaignListStoredFilters } from '@features/campaigns/list/types/campaignListStoredFilters.ts';
import { UserId } from '@shared/types/users.ts';

export type CampaignListFilterKey = keyof CampaignListStoredFilters;

export type CampaignListFilterDefinitionKey = Exclude<CampaignListFilterKey, 'advertiserLabels'>;

export const readCampaignListFilters = (userId: UserId): CampaignListStoredFilters | null => {
  const storageKey = getCampaignListFiltersStorageKey(userId);
  const storedValue = localStorage.getItem(storageKey);

  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue) as CampaignListStoredFilters;
  } catch {
    return null;
  }
};

export const writeCampaignListFilters = (userId: UserId, filters: CampaignListStoredFilters): void => {
  const storageKey = getCampaignListFiltersStorageKey(userId);
  const hasAnyFilter = Object.keys(filters).length > 0;

  if (!hasAnyFilter) {
    localStorage.removeItem(storageKey);
    return;
  }

  localStorage.setItem(storageKey, JSON.stringify(filters));
};

export const getCampaignListFilter = (
  userId: UserId,
  filterName: CampaignListFilterDefinitionKey,
): string | undefined => {
  const storedFilters = readCampaignListFilters(userId);

  return storedFilters?.[filterName];
};

export const updateCampaignListFilter = (
  userId: UserId,
  filterName: CampaignListFilterDefinitionKey,
  filterValue: string,
): void => {
  const storedFilters = readCampaignListFilters(userId) ?? {};

  writeCampaignListFilters(userId, {
    ...storedFilters,
    [filterName]: filterValue,
  });
};

export const removeCampaignListFilter = (userId: UserId, filterName: CampaignListFilterDefinitionKey): void => {
  const storedFilters = readCampaignListFilters(userId);

  if (!storedFilters) {
    return;
  }

  const { [filterName]: removedFilter, ...remainingFilters } = storedFilters;

  if (!removedFilter) {
    return;
  }

  writeCampaignListFilters(userId, remainingFilters);
};
